import React, { useState, FormEvent } from 'react';
import validator from 'validator';
import { FaUser, FaEnvelope, FaPhone, FaComment, FaChevronDown } from 'react-icons/fa';
import contactService from '../../services/api/contact.service';
import toast from 'react-hot-toast';

interface ContactFormData {
  full_name: string;
  phone_no: string;
  email: string;
  issue: string;
  message: string;
}

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    full_name: '',
    phone_no: '',
    email: '',
    issue: '',
    message: ''
  });

  const [errors, setErrors] = useState<Partial<ContactFormData>>({});

  const issueTypes = [
    'General Inquiry',
    'Technical Support',
    'Billing Question',
    'Partnership',
    'Other'
  ];

  const validateForm = (): boolean => {
    const newErrors: Partial<ContactFormData> = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!validator.isEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!validator.isMobilePhone(formData.phone_no, 'en-IN')) {
      newErrors.phone_no = 'Invalid phone number';
    }

    if (!formData.issue) {
      newErrors.issue = 'Please select an issue';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      // Here you would typically send the form data to a backend service
      const response = await contactService.postContact(formData);
      setFormData({
        full_name: '',
        phone_no: '',
        email: '',
        issue: '',
        message: ''
      });
      toast.success('Thank you for your message. We will get back to you soon!');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="w-full max-w-4xl p-5">
      <h2 className="text-3xl font-bold mb-8">
        Contact Us
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Grid container for two-column layout on lg screens and above */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Full Name */}
          <div>
            <label htmlFor="full_name" className="block text-gray-800 font-medium mb-2">
              <FaUser className="inline-block mr-2" /> Full Name
            </label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 
                           ${errors.full_name
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-primary-500'}`}
              placeholder="Enter your full name"
            />
            {errors.full_name && (
              <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-gray-800 font-medium mb-2">
              <FaEnvelope className="inline-block mr-2" /> Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 
                           ${errors.email
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-primary-500'}`}
              placeholder="Enter your email"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Phone Number */}
          <div>
            <label htmlFor="phone_no" className="block text-gray-800 font-medium mb-2">
              <FaPhone className="inline-block mr-2" /> Phone Number
            </label>
            <input
              type="tel"
              id="phone_no"
              name="phone_no"
              value={formData.phone_no}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 
                           ${errors.phone_no
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-primary-500'}`}
              placeholder="Enter your phone number"
            />
            {errors.phone_no && (
              <p className="text-red-500 text-sm mt-1">{errors.phone_no}</p>
            )}
          </div>

          {/* Issue Type */}
          <div>
            <label htmlFor="issue" className="block text-gray-800 font-medium mb-2">
              <FaChevronDown className="inline-block mr-2" /> Issue Type
            </label>
            <select
              id="issue"
              name="issue"
              value={formData.issue}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 
                           ${errors.issue
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-primary-500'}`}
            >
              <option value="">Select an issue type</option>
              {issueTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            {errors.issue && (
              <p className="text-red-500 text-sm mt-1">{errors.issue}</p>
            )}
          </div>
        </div>

        {/* Message (Single Row) */}
        <div>
          <label htmlFor="message" className="block text-gray-800 font-medium mb-2">
            <FaComment className="inline-block mr-2" /> Message
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 
                         ${errors.message
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-primary-500'}`}
            placeholder="Write your message here"
          ></textarea>
          {errors.message && (
            <p className="text-red-500 text-sm mt-1">{errors.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            className="w-full bg-primary-900 text-white py-3 rounded-md 
                         hover:bg-primary-800 transition-colors duration-300 
                         focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Send Message
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;